import React from 'react';
import { Grid, Typography } from '@mui/material';
import { QuickLinks } from '../components/QuickLinks';
import { OcdTypeFilterWithAddButton } from '../components/OcdTypeFilterWithAddButton';
import { useApiRepo, Endpoints } from '../utils/Api';
import { LinkButton } from '../components/LinkButton';

import treatmentPlanImage from '../assets/ocdfeat-treatment-plan-manager.svg';
import squiggle from '../assets/squiggle.jpeg';
import { CardHomework } from '../components/CardHomework';
import {
  Homework as HomeworkObj,
  isERPHomework,
  isTaskHomework
} from '../utils/Homework';
import HomeworkDialog from '../components/HomeworkDialog';

const titleStyle = {
  fontSize: '36pt',
  color: '#010d0d'
};

const textStyle = {
  fontSize: '18pt',
  color: '#010d0d'
};

export function Homework() {
  const { getObjectsFromAPI } = useApiRepo();
  const [homework, setHomework] = React.useState<HomeworkObj[]>([]);
  const [ocdTypeFilter, setOCDTypeFilter] = React.useState<string[]>([]);

  // Modal
  const [open, setOpen] = React.useState(false);

  async function getHomework() {
    const api = await getObjectsFromAPI();

    let page = 0;
    let hasMore = true;
    const pageSize = 100;
    const data = [];

    // fetch all pages
    while (hasMore) {
      // eslint-disable-next-line no-await-in-loop
      const res = await api.get(
        `${Endpoints.homework}?status=active&page=${page}&pageSize=${pageSize}`
      );
      hasMore = res.data.data.length === pageSize;
      page += 1;
      data.push(...res.data.data);
    }

    setHomework(data);
  }

  const modalOpen = () => setOpen(true);
  const modalClose = (newHomework?: HomeworkObj) => {
    if (newHomework) {
      setHomework((prevState) => {
        const updatedHomework = [...prevState, newHomework];
        return updatedHomework;
      });
    }

    setOpen(false);
  };

  React.useEffect(() => {
    getHomework();
  }, []);

  const handleFilters = (displayFilters: any[]) => {
    setOCDTypeFilter(displayFilters);
  };

  const handleTrialChange = (
    event: any,
    homeworkId: string,
    trialId: string
  ) => {
    const { id, value } = event.target;
    setHomework((prevState) => {
      const updatedHomework = prevState.map((hw) => {
        if (hw.id === homeworkId && isERPHomework(hw)) {
          return {
            ...hw,
            trials: hw.trials?.map((trial) =>
              trial.id === trialId
                ? { ...trial, [id]: value === '' ? null : value }
                : trial
            )
          };
        }
        return hw; // Return unchanged homework if not matching
      });
      return updatedHomework;
    });
  };

  const handleTrialUpdate = async (homeworkId: string, trialId: string) => {
    const api = await getObjectsFromAPI();

    // get homework
    const h = homework.find((hw) => hw.id === homeworkId);
    if (h && isERPHomework(h)) {
      // find trial
      const trial = h.trials?.find((t) => t.id === trialId);
      if (trial) {
        await api.post(Endpoints.homeworkTrial(homeworkId), trial);
      }
    }
  };

  const handleTaskChange = (event: any, homeworkId: string) => {
    const { id, value } = event.target;
    setHomework((prevState) => {
      const updatedHomework = prevState.map((hw) => {
        if (hw.id === homeworkId && isTaskHomework(hw)) {
          return {
            ...hw,
            task: {
              ...hw.task,
              [id]: value === '' ? null : value
            }
          };
        }
        return hw; // Return unchanged homework if not matching
      });
      return updatedHomework;
    });
  };

  const handleTaskUpdate = async (homeworkId: string) => {
    const api = await getObjectsFromAPI();

    // get homework
    const h = homework.find((hw) => hw.id === homeworkId);
    if (h && isTaskHomework(h)) {
      await api.post(Endpoints.homeworkTask(homeworkId), h.task);
    }
  };

  const newHomework = {
    canceled: false,
    homeworkType: null,
    description: undefined,
    dueDate: undefined
  };

  return (
    <Grid
      container
      spacing={2}
      direction="row"
      style={{ padding: '50px 0', width: '90vw', margin: 'auto' }}
    >
      <Grid container item xs={12} style={{ padding: '30px' }}>
        <Grid item xs={12} lg={6}>
          <Typography variant="h3" style={titleStyle}>
            Homework
          </Typography>
          <Grid item style={{ padding: '40px 0px 40px' }}>
            <img src={squiggle} alt="divderImage" style={{ height: '15px' }} />
          </Grid>
          <Typography
            variant="h6"
            style={textStyle}
            sx={{ display: { xs: 'none', lg: 'cover' } }}
          >
            If you&rsquo;re using the Treatment Plan Manager, it means
            you&rsquo;re working hard to confront the thoughts, images, objects,
            situations, or whatever makes you anxious - congratulations on
            taking this step in your journey. Whether you&rsquo;re working with
            a therapist or managing the exposure homework on your own, this tool
            will help you keep track of activities, share them with your
            therapist and see your progress over time. Keep up the great work!
          </Typography>
          <Grid item style={{ padding: '40px 0px 40px' }}>
            <LinkButton buttonText="+ Add Homework" handleClick={modalOpen} />
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
          lg={6}
          justifyContent="center"
          sx={{ display: { xs: 'none', lg: 'cover' } }}
        >
          <img
            src={treatmentPlanImage}
            style={{ height: '500px' }}
            alt="tileImage"
          />
        </Grid>
      </Grid>
      <HomeworkDialog homework={newHomework} open={open} onClose={modalClose} />

      <Grid item xs={12}>
        <OcdTypeFilterWithAddButton onChangeFilter={handleFilters}>
          <Grid
            container
            item
            direction="row"
            justifyContent="left"
            spacing={2}
          >
            {homework
              .filter(
                (hw) =>
                  ocdTypeFilter.length
                    ? ocdTypeFilter.includes(hw.obsessionTypeName!)
                    : true // don't filter
              )
              .sort((a, b) => {
                const dateA = new Date(a.dueDate!);
                const dateB = new Date(b.dueDate!);
                return dateA.getTime() - dateB.getTime(); // Sort in ascending order
              })
              .map((hw) => (
                <CardHomework
                  key={hw.id}
                  homework={hw}
                  onTaskChange={handleTaskChange}
                  onTaskUpdate={handleTaskUpdate}
                  onTrialChange={handleTrialChange}
                  onTrialUpdate={handleTrialUpdate}
                />
              ))}
          </Grid>
        </OcdTypeFilterWithAddButton>
      </Grid>
      <Grid item xs={12}>
        <QuickLinks />
      </Grid>
    </Grid>
  );
}
